import React, { ChangeEvent, CSSProperties, Fragment, useCallback, useMemo, useRef, useState } from 'react'
import {
  CellContext,
  createColumnHelper as createColumnHelperFn,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  Header,
  HeaderContext,
  Row,
  SortingState,
  useReactTable,
  ExpandedState,
  functionalUpdate,
  ColumnSizingState,
  Updater,
  HeaderGroup,
  RowData,
  RowSelectionState,
} from '@tanstack/react-table'
import { Table as TableStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import clsx from 'clsx'
import useSize from '../../lib/hooks/useSize'
import useUpdateEffect from '../../lib/hooks/useUpdateEffect'
import { TableSortLabel } from '../Table'
import Pagination, { PaginationProps } from '../Pagination'
import Spinner from '../Spinner'
import useRemFactor from '../../lib/hooks/useRemFactor'
import { getScrollbarWidth } from '../../lib/utils'
import { EXPAND_COLUMN_ID, expandableColumn } from './helper/expandable'
import { SELECT_COLUMN_ID, selectableColumn } from './helper/selectable'
import { FixedSizeList } from 'react-window'

// re exported types from tanstack library
export const createColumnHelper = createColumnHelperFn
export type DataTableRowData = RowData
export type DataTableCellContext<TData extends RowData, TValue = any> = CellContext<TData, TValue>
export type DataTableHeaderContext<TData extends RowData, TValue = any> = HeaderContext<TData, TValue>
export type DataTableSelectionMode = 'all' | 'page' | undefined
export type DataTableRowSelectionState = RowSelectionState
export type DataTableSortingState = SortingState
export type DataTableColumnDef<TData extends RowData, TValue = any> = ColumnDef<TData, TValue> & {
  /** column text alignment */
  align?: 'right' | 'left'
  /** set to true to disable resizing */
  disableResizing?: boolean
  /** set to true to disable sorting */
  disableSortBy?: boolean
}
export type DataTableExpandedState = ExpandedState
export type DataTableRow<TData extends RowData> = Row<TData>

// parameter of `onQueryUpdate` callback
export type DataTableQuery = {
  pageIndex: number
  pageSize: number
  sortBy: DataTableSortingState
}

// parameter of `onStateUpdate` callback
export type DataTableState<T extends RowData> = {
  rowsPerPage: number
  columns: Array<DataTableColumnDef<T, any>>
  sortBy: DataTableSortingState
}

export type DataTableEmptyOptions = {
  /** optional empty state label */
  label?: string
  /** optional additional content to display */
  content?: React.ReactNode | undefined
}

// options of expandable feature
export type DataTableExpandableOptions<T extends RowData> = {
  /**
   * Predefined expanded rows
   * this should be using table and its children elements only. otherwise there is a warning
   * validateDomString
   */
  expandedRows?: DataTableExpandedState
  /**
   * Use this method to set conditional height based on row data
   * @param row expanded row data
   * @returns expanded row height (pixels)
   */
  getRowHeight?: (row: DataTableRow<T>) => number
  /**
   * Height of expanded rows
   * @default 40
   */
  height?: number
  /**
   * allow to enable expandable feature at row level
   * @param row
   * @returns true if row is expandable
   */
  isRowExpandable?: (row: DataTableRow<T>) => boolean
  /**
   * triggered on open and close of expandable rows
   * @param expandedRows new expanded rows
   * @returns
   */
  onChange?: (expandedRows: DataTableExpandedState) => void
  /**
   * render function for expanded row
   * @param row expanded row data
   */
  renderExpanded?: (row: DataTableRow<T>) => JSX.Element
}

export interface DataTableProps<T extends object = {}> {
  /**
   * Allow row dividers
   * @default false
   */
  allowDividers?: boolean
  /**
   * Allow column resizing
   * @default false
   */
  allowResize?: boolean
  /**
   * Allow virtualization for huge lists.
   * You must set a fixed height when using this option.
   * @default false
   */
  allowVirtualization?: boolean
  className?: string
  /**
   * Condensed mode with smaller row heights.
   * This mode can display more informations in less space.
   * @default false
   */
  condensed?: boolean
  /**
   * Columns configuration object
   */
  columns: DataTableColumnDef<T, any>[]
  /**
   * Table data
   */
  data: T[]
  /**
   * Set this option to true to output table debugging information to the console (only in dev mode)
   */
  debugTable?: boolean
  /**
   * Default rows per page
   * @default 10
   */
  defaultRowsPerPage?: number
  /**
   * Disable pagination. It can be managed externally instead.
   * @default false
   */
  disablePagination?: boolean
  /**
   * empty state customization options
   */
  emptyOptions?: DataTableEmptyOptions
  /**
   * Enable expandable rows. Cannot be used with virtualization.
   * @default false
   */
  expandable?: boolean
  /**
   * expandable rows options
   * @default { height: 40, renderExpanded: () => <></> }
   */
  expandableOptions?: DataTableExpandableOptions<T>
  /**
   * Optional fixed height.
   * Required with `useVirtualization`
   */
  height?: number
  /**
   * Loading state (controlled mode)
   * @default false
   */
  loading?: boolean
  /**
   * Pasing a number greater than 1 will enable multisort.
   * Limit on max number of columns for multisort.
   * If set to 1 it will switch to single column sort.
   */
  maxSortColCount?: number
  /**
   * Data query udpate callback<br />
   * If defined, the DataTable will run in **controlled mode**, assuming you set `loading`, `pageCount` and `rowCount` values manually<br />
   *
   * `DataTableQuery: { pageIndex: number, pageSize: number, sortBy: [] }`
   */
  onQueryUpdate?: (query: DataTableQuery) => void
  /**
   * Row click callback.<br />
   * Enable single selection mode.<br />
   * When used in combination with `onRowSelection`, clicking on a row won't select it.
   */
  onRowClick?: (row: T) => void
  /**
   * Enables and returns multiple row selection
   */
  onRowSelection?: (rows: Array<DataTableRow<T>>) => void
  /**
   * State update callback<br />
   * Useful to implement DataTable state persistence (like localStorage)
   *
   * `DataTableState: { columns: [{ id: string, size: number }] }`
   */
  onStateUpdate?: (state: DataTableState<T>) => void
  /**
   * Total page count (controlled mode)
   */
  pageCount?: number
  /**
   * All props that can be passed to pagination component, can be passed here. Certain mandatory props are
   * already passed internally.<br /> use `defaultRowsPerPage` instead of rowsPerPage.<br />
   * To control pagination, you need to pass `page` and `onPageChange` props.
   */
  paginationProps?: Partial<PaginationProps>
  /**
   * Total row count (controlled mode)
   * @default data.length
   */
  rowCount?: number
  /**
   * Define initial sorted columns
   * @type DataTableSortingState
   */
  sortBy?: DataTableSortingState
  /**
   * Define the behaviour of select-all checkbox in MultiRowSelection
   * By default all rows in the table are selected. <br />
   * selectionMode: "page" selects only the rows in the current page.
   * @default "all"
   */
  selectionMode?: DataTableSelectionMode
  /**
   * default selected rows.
   */
  defaultSelectedRows?: DataTableRowSelectionState
  /**
   * By default, the row id for any row is simply the `row.index`. If you need a useful row
   * identifier, you can use getRowId function that returns unique ID for each row. <br />
   * When undefined, initial selection of rows is based on index.
   * @default undefined
   */
  getRowId?: (originalRow: T, index: number, parent?: Row<T>) => string
}

const useStyles = createUseStyles(TableStyles, 'datatable')

const ROW_HEIGHT_DEFAULT = 40
const ROW_HEIGHT_CONDENSED = 32
const SPACELESS_COLUMNS = [SELECT_COLUMN_ID, EXPAND_COLUMN_ID]

const DataTable = <T extends Record<string, any>>(props: DataTableProps<T>) => {
  const {
    allowDividers,
    allowResize = false,
    allowVirtualization,
    className,
    columns: baseColumns,
    condensed,
    data,
    debugTable = false,
    defaultRowsPerPage = 10,
    defaultSelectedRows,
    disablePagination = false,
    emptyOptions = {},
    expandable = false,
    expandableOptions: expandableOptionsProp,
    getRowId,
    height: fixedHeight,
    loading,
    maxSortColCount,
    onQueryUpdate,
    onRowClick,
    onRowSelection,
    onStateUpdate,
    pageCount: controlledPageCount = -1,
    paginationProps = {},
    rowCount,
    selectionMode = 'all',
    sortBy: initialSortBy,
  } = props

  const classes = useStyles()
  const defaultColumn = React.useMemo(
    () => ({
      // When using the useFlexLayout:
      minSize: 60, // minWidth is only used as a limit for resizing
      size: 150, // width is used for both the flex-basis and flex-grow
      maxSize: 400, // maxWidth is only used as a limit for resizing
    }),
    [],
  )

  // default expandable options merged with specified options
  const expandableOptions = useMemo(
    () => ({
      expandedRows: {},
      height: 40,
      isRowExpandable: () => true,
      onChange: expandableOptionsProp?.onChange,
      renderExpanded: () => <></>,
      ...expandableOptionsProp,
    }),
    [expandableOptionsProp],
  )

  const rootRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const headRef = useRef<HTMLDivElement>(null)
  const rootSize = useSize(rootRef)
  const scrollbarSize = useMemo(() => getScrollbarWidth(), [])

  // row height values adapted to browser font size setting
  const remFactor = useRemFactor() //can we not infer
  const rowHeightDefault = (ROW_HEIGHT_DEFAULT / 16) * remFactor
  const rowHeightCondensed = (ROW_HEIGHT_CONDENSED / 16) * remFactor

  // passing the sortBy to preload the datatable with sort applied
  const [sorting, setSorting] = useState<DataTableSortingState>(initialSortBy ? initialSortBy : [])
  // resized columns
  const [columnResizing, setColumnResizing] = useState<ColumnSizingState>({})

  // manage rowSelection
  const [rowSelection, setRowSelection] = useState<DataTableRowSelectionState>(defaultSelectedRows || {})

  // memoized columns with optional expand/selection columns
  const columns = useMemo(
    () => [
      ...(expandable ? [expandableColumn] : []), //condition to show expandable
      ...(onRowSelection ? [selectableColumn] : []), //condition to show checkbox
      ...baseColumns,
    ],
    [baseColumns, expandable, onRowSelection],
  )

  // controlled mode check
  const isControlled = useMemo(() => onQueryUpdate !== undefined, [onQueryUpdate])

  /*
  pagination notes
  when setting manualPagination:true, we need to manage the following,
  pageCount, controlledPagination
  */
  const [controlledPagination, setControlledPagination] = useState({
    pageIndex: 0,
    pageSize: defaultRowsPerPage,
  })

  // expanded rows
  const [expanded, setExpanded] = useState<DataTableExpandedState>(expandableOptions.expandedRows)

  const handleExpandedChange = (updaterOrValue: Updater<DataTableExpandedState> | DataTableExpandedState) => {
    if (typeof updaterOrValue !== 'function') {
      const expandedRows: any = {}
      if (updaterOrValue === true) {
        rows.map((_, index) => (expandedRows['' + index] = true))
      }
      if (expandableOptions.onChange) expandableOptions.onChange(expandedRows)
      setExpanded(expandedRows)
    } else {
      const newExpanded = functionalUpdate(updaterOrValue, expanded)
      if (expandableOptions.onChange) expandableOptions.onChange(newExpanded)
      setExpanded(newExpanded)
    }
  }

  const { getState, setPageSize, resetPageSize, setPageIndex, resetPageIndex, ...table } = useReactTable({
    columns,
    columnResizeMode: 'onChange',
    data,
    getRowId,
    initialState: {
      sorting: initialSortBy,
      columnSizing: columnResizing,
      pagination: {
        pageIndex: 0,
        pageSize: allowVirtualization ? data.length : Number(defaultRowsPerPage),
      },
      ...(defaultSelectedRows && { rowSelection }),
    },
    autoResetPageIndex: true,
    ...(isControlled && {
      manualPagination: true,
      autoResetPageIndex: false,
      onPaginationChange: setControlledPagination, //needed for controlled pagination
      manualSorting: true,
      pageCount: controlledPageCount,
    }),
    enableSorting: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableSortingRemoval: true,
    enableMultiSort: true,
    ...(maxSortColCount && {
      maxMultiSortColCount: maxSortColCount,
    }),
    ...(expandable && {
      enableExpanding: true,
      getRowCanExpand: expandableOptions.isRowExpandable,
      onExpandedChange: handleExpandedChange,
    }),
    onColumnSizingChange: setColumnResizing,
    state: {
      sorting: sorting,
      ...(expandable && { expanded: expanded }),
      ...(isControlled && { pagination: controlledPagination }),
      columnSizing: columnResizing,
      rowSelection: rowSelection,
    },
    defaultColumn: defaultColumn,
    debugTable,
    enableColumnResizing: allowResize,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSubRows: (row) => row.subRows,
    meta: {
      selectionMode,
    },
  })

  // parse table data/state
  const {
    pagination: { pageSize, pageIndex },
    columnSizingInfo: { isResizingColumn },
  } = getState()
  const rows = table.getRowModel().rows
  const paginationRows = table.getPaginationRowModel().rows
  const selectedRows = table.getSelectedRowModel().flatRows
  const totalSize = table.getTotalSize()

  // clear selection when selectionMode changes
  useUpdateEffect(() => table.toggleAllRowsSelected && table.toggleAllRowsSelected(false), [selectionMode])

  // controlled defaultSelectedRows
  // TODO we should allow users to control row selection through dedicated prop
  useUpdateEffect(() => {
    // reset selection when defaultSelectedRows changes to empty
    if (!defaultSelectedRows || Object.keys(defaultSelectedRows).length === 0) table.resetRowSelection(true)
  }, [defaultSelectedRows])

  // dispatch simplified table state for persistence (excluding first render)
  useUpdateEffect(
    function stateUpdateEffect() {
      if (onStateUpdate && !isResizingColumn) {
        onStateUpdate({
          rowsPerPage: pageSize,
          columns: table.getAllColumns().map((column) => ({
            // add more information that needs to be persisited
            id: column.id,
            size: column.getSize(),
          })),
          sortBy: sorting,
        })
      }
    },
    // we don't include onStateUpdate callback in dependencies
    // to avoid useEffect/useState infinite loops
    // because onStateUpdate will be a new function reference in each render
    // except if parent component memoized it with useCallback for example
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageSize, isResizingColumn, sorting],
  )

  // manual pagination works using pagination in state, pageCount
  // in controlled mode, changing sort should reset page index
  const onQueryUpdateHash = () =>
    JSON.stringify({
      pageIndex,
      pageSize,
      sorting,
    })
  const onQueryUpdateRef = useRef(onQueryUpdateHash())
  const sortByRef = useRef(sorting)
  useUpdateEffect(() => {
    if (!onQueryUpdate) return

    // ensure we don't dispatch if nothing has changed
    const queryHash = onQueryUpdateHash()
    if (onQueryUpdateRef.current === queryHash) return
    onQueryUpdateRef.current = queryHash

    // reset pageIndex if sorting is updated
    if (!sortByRef.current) sortByRef.current = sorting
    else if (sortByRef.current !== sorting) {
      resetPageIndex()
      sortByRef.current = sorting
    }

    onQueryUpdate({
      pageIndex,
      pageSize,
      sortBy: sorting,
    })
  }, [onQueryUpdate, pageIndex, pageSize, sorting])

  // call onRowSelection selected rows, when multi selection is enabled
  useUpdateEffect(() => {
    onRowSelection?.(selectedRows.map((d: any) => d.original))
  }, [onRowSelection, selectedRows])

  // horizontal scrolling synchronisation between body/head
  const [hscroll, setHScroll] = useState(false)
  const handleScroll = (evt: any) => {
    if (bodyRef.current && headRef.current) {
      const scroll = evt && evt.target ? evt.target.scrollLeft : bodyRef.current.scrollLeft
      headRef.current.scrollLeft = scroll
      setHScroll(scroll > 0)
    }
  }

  // computed styles and sizes
  const totalRowsHeight = useMemo(() => {
    const rowHeight = condensed ? rowHeightCondensed : rowHeightDefault
    const expandedRows = rows.filter((row) => row.getCanExpand() && row.getIsExpanded())
    const expandedRowsCount = expandable ? expandedRows.length : 0
    let totalExpandedRowsHeight = expandedRowsCount * expandableOptions.height
    // getRowHeight defined > compute height for each expanded rows
    if (expandableOptions.getRowHeight) {
      const getHeight = expandableOptions.getRowHeight
      totalExpandedRowsHeight = expandedRows.reduce((acc: number, row: DataTableRow<T>, index: number) => {
        return acc + getHeight(row)
      }, 0)
    }
    return rows.length * rowHeight + totalExpandedRowsHeight
  }, [condensed, expandable, expandableOptions, rowHeightCondensed, rowHeightDefault, rows])

  const minBodyHeight = useMemo(() => {
    const headerHeight = (condensed ? rowHeightCondensed : rowHeightDefault) + (9 * remFactor) / 16
    const hasHScroll = totalSize > rootSize.width
    const totalHeight = totalRowsHeight + (hasHScroll ? scrollbarSize : 0)

    if (fixedHeight) return Math.min(totalHeight, fixedHeight - headerHeight)
    else return totalHeight
  }, [
    condensed,
    fixedHeight,
    remFactor,
    rootSize,
    rowHeightCondensed,
    rowHeightDefault,
    scrollbarSize,
    totalRowsHeight,
    totalSize,
  ])

  const headerStyle = useMemo(() => {
    const hasScroll = hscroll || totalRowsHeight > minBodyHeight
    return {
      minWidth: totalSize + (hasScroll ? scrollbarSize : 0),
      paddingRight: hasScroll ? scrollbarSize : 0,
    }
  }, [minBodyHeight, hscroll, scrollbarSize, totalRowsHeight, totalSize])

  const renderHeader = useCallback(
    (headerGroup: HeaderGroup<T>) => {
      const commonHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      }

      return (
        <div key={headerGroup.id} className={classes.row} role='row' style={{ display: 'flex', ...headerStyle }}>
          {headerGroup.headers.map((header: Header<T, any>) => (
            <div
              key={header.id}
              role='columnheader'
              className={clsx(classes.cell_head, {
                [classes.cell_right]: header.column.columnDef.align === 'right',
                [classes.cell_expand]: expandable && header.id === EXPAND_COLUMN_ID,
                [classes.cell_checkbox]: onRowSelection && header.column.id === SELECT_COLUMN_ID,
              })}
              style={{
                width: SPACELESS_COLUMNS.includes(header.id) ? undefined : header.column.getSize(),
                minWidth: SPACELESS_COLUMNS.includes(header.id) ? undefined : header.column.columnDef.minSize,
                ...(!SPACELESS_COLUMNS.includes(header.id) && {
                  flex: `${header.column.getSize()} 0 auto`,
                }), //dont apply flex to action column
                height: '100%',
              }}
            >
              {header.column.columnDef.disableSortBy ? (
                <div style={{ ...commonHeaderStyle }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </div>
              ) : (
                <TableSortLabel
                  active={!!header.column.getIsSorted()}
                  align={header.column.columnDef.align === 'right' ? 'right' : 'left'}
                  direction={header.column.getIsSorted()}
                  index={header.column.getCanMultiSort() ? header.column.getSortIndex() : undefined}
                  style={{
                    ...commonHeaderStyle,
                    cursor: header.column.columnDef.disableSortBy ? 'pointer' : undefined,
                  }}
                  onClick={() => {
                    header.column.toggleSorting(undefined, true)
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableSortLabel>
              )}
              {allowResize && header.column.columnDef.disableResizing !== true && (
                <div
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  className={clsx(classes.resizer, {
                    [classes.resizer_resizing]: header.column.getIsResizing(),
                  })}
                ></div>
              )}
            </div>
          ))}
        </div>
      )
    },
    [allowResize, classes, expandable, headerStyle, onRowSelection],
  )

  const renderRow = useCallback(
    (row: DataTableRow<T>, style?: CSSProperties) => {
      const expandedStyles = {
        ...style,
        height: expandableOptions.getRowHeight ? expandableOptions.getRowHeight(row) : expandableOptions.height,
      }
      return (
        <Fragment key={row.id}>
          <div
            role='row'
            className={clsx(classes.row, {
              [classes.row_dividers]: allowDividers,
              [classes.row_clickable]: onRowClick || onRowSelection,
              [classes.row_selected]: row.getIsSelected(),
            })}
            style={{
              display: 'flex',
              minWidth: totalSize,
              ...style,
            }}
            onClick={(e) => {
              if (onRowClick) {
                onRowClick(row.original)
                // single row selection mode
                if (!onRowSelection) {
                  table.toggleAllRowsSelected(false)
                  row.toggleSelected()
                }
              }
              // when clicking outside checkbox, rowSelection will be called only if onRowClick is falsy
              if (
                e.target &&
                (e.target as HTMLElement).getAttribute('ads-cell-type') !== SELECT_COLUMN_ID &&
                !onRowClick
              ) {
                // onRowSelection is called in the hooks which is triggered by toggleSelected function
                onRowSelection && row.toggleSelected()
              }
            }}
            {...(row.getIsExpanded() && { 'aria-expanded': 'true' })}
          >
            {row.getVisibleCells().map((cell) => {
              return (
                <div
                  role='cell'
                  key={cell.id}
                  className={clsx({
                    [classes.cell]: !SPACELESS_COLUMNS.includes(cell.column.id),
                    [classes.cell_right]: cell.column.columnDef.align === 'right',
                    [classes.cell_checkbox]: onRowSelection && cell.column.id === SELECT_COLUMN_ID,
                    [classes.cell_expand]: expandable && cell.column.id === EXPAND_COLUMN_ID,
                  })}
                  style={{
                    width: SPACELESS_COLUMNS.includes(cell.column.id) ? undefined : cell.column.getSize(),
                    margin: SPACELESS_COLUMNS.includes(cell.column.id) ? 'unset' : undefined,
                    ...(!SPACELESS_COLUMNS.includes(cell.column.id) && {
                      flex: `${cell.column.getSize()} 0 auto`,
                    }), //dont apply flex to action column
                    minWidth: SPACELESS_COLUMNS.includes(cell.column.id) ? undefined : cell.column.columnDef.minSize,
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              )
            })}
          </div>
          {expandable && row.getCanExpand() && row.getIsExpanded() && (
            <div className={clsx(classes.row_expanded)} role='row' style={expandedStyles}>
              <div role='cell' aria-label={`Expanded row ${row.id}`} aria-colspan={columns.length}>
                {expandableOptions.renderExpanded(row)}
              </div>
              <div role='row' aria-hidden='true' className={clsx(classes.row_expanded_even)}></div>
            </div>
          )}
        </Fragment>
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowDividers, columns.length, classes, expandable, expandableOptions, onRowClick, onRowSelection, totalSize],
  )

  const renderVirtualizedRows = useCallback(
    () => (
      <FixedSizeList
        height={minBodyHeight}
        width={rootSize.width}
        itemCount={paginationRows.length}
        innerElementType={''}
        itemSize={condensed ? rowHeightCondensed : rowHeightDefault}
        onScroll={handleScroll}
      >
        {({ index, style }) =>
          renderRow(paginationRows[index], {
            ...style,
            width: 'inherit',
            minWidth: totalSize,
          })
        }
      </FixedSizeList>
    ),
    [condensed, minBodyHeight, paginationRows, renderRow, rootSize, rowHeightCondensed, rowHeightDefault, totalSize],
  )

  const renderPagination = useCallback(() => {
    if (disablePagination) return null
    const {
      onPageChange: controlledOnPageChange,
      onRowsPerPageChange,
      page: controlledPageIndex,
      showGoto,
      showRowsPerPage,
      ...restPagination
    } = paginationProps

    // In react-table v8, we are manually calculating the information for pagination
    const count = rowCount || data.length

    // hide pagination automatically depending on page count
    // except if showRowsPerPage is enabled
    if (!showRowsPerPage && (count <= pageSize || controlledPageCount === 1)) return null

    const startRow = pageSize * pageIndex + 1 //pageIndex starts from 0
    const endRow = Math.min((pageIndex + 1) * pageSize, count)

    // start from 1, when pagesize is altered
    const handleRowsPerPageChange = (evt: ChangeEvent<HTMLSelectElement>, val: string) => {
      const pageSize = parseInt(val)
      setPageSize(pageSize)
      resetPageIndex()
      if (onRowsPerPageChange) onRowsPerPageChange(evt, pageSize)
    }

    return (
      <Pagination
        className={classes.pagination}
        legend={`${startRow}-${endRow} of ${count}`}
        rowsPerPage={pageSize}
        count={count}
        page={controlledPageIndex || pageIndex + 1}
        rowsPerPageLabel='Rows per page'
        onPageChange={(event, num) => {
          setPageIndex(Number(num) - 1)
          selectionMode === 'page' && table.toggleAllRowsSelected(false)
          controlledOnPageChange && controlledOnPageChange(event, num)
        }}
        onRowsPerPageChange={showRowsPerPage ? handleRowsPerPageChange : undefined}
        showGoto={showGoto}
        {...restPagination}
      />
    )
  }, [
    setPageSize,
    pageSize,
    resetPageIndex,
    setPageIndex,
    pageIndex,
    data.length,
    controlledPageCount,
    rowCount,
    disablePagination,
    paginationProps,
    classes.pagination,
    selectionMode,
    table,
  ])

  const isEmpty = useMemo(() => rows.length === 0, [rows])

  return (
    <div ref={rootRef} className={classes.container}>
      <div
        role='table'
        className={clsx(className, classes.root, {
          [classes._condensed]: condensed,
        })}
        style={{ display: 'flex', flexDirection: 'column', minWidth: '0px' }}
      >
        <div className={classes.head} ref={headRef} role='rowgroup' style={{ width: rootSize.width }}>
          {table.getHeaderGroups().map((headerGroup) => renderHeader(headerGroup))}
        </div>
        <div
          className={classes.body}
          ref={bodyRef}
          role='rowgroup'
          style={{
            width: rootSize.width,
            height: isEmpty ? 'auto' : minBodyHeight,
          }}
          onScroll={handleScroll}
        >
          {isEmpty ? (
            <div className={classes.empty} role='row'>
              <div role='cell'>
                {emptyOptions.label && <p>{emptyOptions.label}</p>}
                <>{emptyOptions.content}</>
              </div>
            </div>
          ) : (
            <>{allowVirtualization ? renderVirtualizedRows() : rows.map((row) => renderRow(row, {}))}</>
          )}
          {loading && (
            <div className={classes.loading}>
              <Spinner />
            </div>
          )}
        </div>
      </div>
      {renderPagination()}
    </div>
  )
}

export default DataTable
